import { apiRequest, apiRequestSSE, ApiError } from '@/shared/api';

export type PlaygroundRole = 'buyer' | 'agent' | 'owner' | 'other';

export interface PlaygroundSession {
  dialog_id: string;
  interlocutor_id: string;
  role?: PlaygroundRole;
  role_source?: 'default' | 'seed' | 'classified' | 'explicit';
}

export interface CreatePlaygroundSessionInput {
  /** Short operator-written background stored as the interlocutor summary. */
  seed?: string;
}

export interface PlaygroundListingAttachment {
  type: 'listing';
  id: string;
  title?: string;
  url?: string;
  photos?: string[];
  price_thb?: string | number;
  usd_price?: string | number;
  price_quote?: string | number;
  quote_currency?: string;
  district?: string;
  area_sqm?: string | number;
  property_type?: string;
}

export type PlaygroundMessageAttachment =
  PlaygroundListingAttachment | { type: string; [key: string]: unknown };

export interface PlaygroundMessage {
  id: string;
  sender: string;
  content: string;
  lifecycle_status: string;
  created_at: string;
  run_id?: string | null;
  attachments?: PlaygroundMessageAttachment[];
}

export interface PlaygroundSendResult {
  messages: PlaygroundMessage[];
  run_id?: string | null;
  run_status?: string | null;
  ai_skipped_pending_approval: boolean;
}

export interface PlaygroundLLMCall {
  purpose: string;
  model?: string;
  request_messages?: { role: string; content: string }[];
  response_content?: string;
  tokens_in?: number;
  tokens_out?: number;
  duration_ms?: number;
  error?: string;
}

export interface PlaygroundSkillStepDiag {
  step_key: string;
  tool: string;
  status: string;
  duration_ms?: number;
  error?: string;
  received?: Record<string, unknown>;
  returned?: Record<string, unknown>;
}

export interface PlaygroundSkillDiag {
  skill_key?: string;
  skill_version_id?: string;
  query_key?: unknown;
  tool_source?: string;
  status: string;
  duration_ms?: number;
  error?: string;
  received?: Record<string, unknown>;
  returned?: Record<string, unknown>;
  steps?: PlaygroundSkillStepDiag[];
}

export interface PlaygroundStepDiag {
  step_key: string;
  action: string;
  status: string;
  duration_ms?: number;
  model?: string;
  tokens_in?: number;
  tokens_out?: number;
  error?: string;
  received?: Record<string, unknown>;
  returned?: Record<string, unknown>;
  llm_calls: PlaygroundLLMCall[];
  skills: PlaygroundSkillDiag[];
}

/** Raw shape persisted by the Go buyer harness (agent_runs.diagnostics). */
export interface PlaygroundRawLLMCall {
  purpose: string;
  model?: string;
  duration_ms?: number;
  tokens_in?: number;
  tokens_out?: number;
  finish_reason?: string;
  tool_calls?: number;
  error?: string;
}

export interface PlaygroundRunDiagnostics {
  /** Normalized view used by the inspector; derived from tool_calls + llm_calls. */
  steps: PlaygroundStepDiag[];
  model?: string;
  duration_ms?: number;
  system_prompt_chars?: number;
  system_prompt_hash?: string;
  allowed_skills?: string[];
  skills_loaded?: string[];
  llm_calls?: PlaygroundRawLLMCall[];
  tool_calls?: PlaygroundToolCallDiag[];
}

export interface PlaygroundToolCallDiag {
  tool: string;
  args?: string;
  result?: string;
  status: string;
  error?: string;
}

function parseJsonRecord(raw: string | undefined): Record<string, unknown> | undefined {
  if (!raw) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return { value: parsed };
  } catch {
    return { raw };
  }
}

function llmStep(
  stepKey: string,
  action: string,
  calls: PlaygroundRawLLMCall[],
  model?: string,
): PlaygroundStepDiag | null {
  if (calls.length === 0) return null;
  const tokensIn = calls.reduce((sum, call) => sum + (call.tokens_in ?? 0), 0);
  const tokensOut = calls.reduce((sum, call) => sum + (call.tokens_out ?? 0), 0);
  const duration = calls.reduce((sum, call) => sum + (call.duration_ms ?? 0), 0);
  const failed = calls.find((call) => call.error);
  return {
    step_key: stepKey,
    action,
    status: failed ? 'failed' : 'succeeded',
    duration_ms: duration || undefined,
    model: calls[0]?.model ?? model,
    tokens_in: tokensIn || undefined,
    tokens_out: tokensOut || undefined,
    error: failed?.error,
    llm_calls: calls.map((call) => ({
      purpose: call.purpose,
      model: call.model,
      tokens_in: call.tokens_in,
      tokens_out: call.tokens_out,
      duration_ms: call.duration_ms,
      error: call.error,
      response_content: call.finish_reason
        ? `finish_reason: ${call.finish_reason}${call.tool_calls ? ` · tool_calls: ${call.tool_calls}` : ''}`
        : undefined,
    })),
    skills: [],
  };
}

/** Folds the flat harness trail into inspector steps: reply LLM loop, tool calls, memory extract. */
export function normalizeRunDiagnostics(raw: PlaygroundRunDiagnostics | undefined) {
  const source: PlaygroundRunDiagnostics = raw ?? { steps: [] };
  if (Array.isArray(source.steps) && source.steps.length > 0) {
    return source;
  }
  const llmCalls = source.llm_calls ?? [];
  const steps: PlaygroundStepDiag[] = [];

  const replyStep = llmStep(
    'reply',
    'agata-sales',
    llmCalls.filter((call) => call.purpose === 'reply'),
    source.model,
  );
  if (replyStep) steps.push(replyStep);

  const toolCalls = Array.isArray(source.tool_calls) ? source.tool_calls : [];
  toolCalls.forEach((call, index) => {
    const tool = call.tool || `tool_${index}`;
    if (call.status === 'succeeded' && !call.args) {
      const open = [...steps]
        .reverse()
        .find((step) => step.action === tool && step.status === 'started');
      if (open) {
        open.status = 'succeeded';
        open.returned = parseJsonRecord(call.result);
        return;
      }
    }
    steps.push({
      step_key: tool,
      action: tool,
      status: call.status || 'succeeded',
      error: call.error,
      received: parseJsonRecord(call.args),
      returned: parseJsonRecord(call.result),
      llm_calls: [],
      skills: [],
    });
  });

  const memoryStep = llmStep(
    'memory_extract',
    'memory_extract',
    llmCalls.filter((call) => call.purpose === 'memory_extract'),
  );
  if (memoryStep) steps.push(memoryStep);

  return { ...source, steps };
}

export interface PlaygroundRun {
  id: string;
  status: string;
  error?: string;
  tokens_in?: number | null;
  tokens_out?: number | null;
  started_at: string;
  finished_at?: string | null;
  context: Record<string, unknown>;
  diagnostics: PlaygroundRunDiagnostics;
}

export async function createPlaygroundSession(
  input: CreatePlaygroundSessionInput = {},
): Promise<PlaygroundSession> {
  const body: CreatePlaygroundSessionInput = {};
  if (input.seed?.trim()) body.seed = input.seed.trim();
  return apiRequest<PlaygroundSession>('/playground/sessions', {
    method: 'POST',
    body: Object.keys(body).length > 0 ? body : undefined,
  });
}

export async function listPlaygroundMessages(dialogId: string): Promise<PlaygroundMessage[]> {
  const data = await apiRequest<{ messages: PlaygroundMessage[] }>(
    `/playground/sessions/${encodeURIComponent(dialogId)}/messages`,
  );
  return data.messages;
}

export async function sendPlaygroundMessage(
  dialogId: string,
  input: { content: string; idempotency_key?: string },
  options?: {
    signal?: AbortSignal;
  },
): Promise<PlaygroundSendResult> {
  let result: PlaygroundSendResult | null = null;
  let streamError: string | null = null;

  await apiRequestSSE(`/playground/sessions/${encodeURIComponent(dialogId)}/messages`, {
    method: 'POST',
    body: input,
    signal: options?.signal,
    onEvent: (event, data) => {
      if (event === 'result' && data && typeof data === 'object') {
        const payload = data as PlaygroundSendResult;
        result = {
          messages: payload.messages ?? [],
          run_id: payload.run_id,
          run_status: payload.run_status,
          ai_skipped_pending_approval: Boolean(payload.ai_skipped_pending_approval),
        };
        return;
      }
      if (event === 'error') {
        if (data && typeof data === 'object' && 'error' in data) {
          const message = (data as { error: unknown }).error;
          streamError = typeof message === 'string' ? message : 'Send failed';
        } else {
          streamError = 'Send failed';
        }
      }
    },
  });

  if (streamError) {
    throw new ApiError(streamError, 500);
  }
  if (!result) {
    throw new ApiError('Playground send ended without a result', 500);
  }
  return result;
}

export async function getPlaygroundRun(dialogId: string, runId: string): Promise<PlaygroundRun> {
  const run = await apiRequest<PlaygroundRun>(
    `/playground/sessions/${encodeURIComponent(dialogId)}/runs/${encodeURIComponent(runId)}`,
  );
  return {
    ...run,
    context: run.context ?? {},
    diagnostics: normalizeRunDiagnostics(run.diagnostics),
  };
}

export interface PlaygroundReminderStep {
  number: number;
  kind: 'push' | 'defer' | string;
  offset: string;
  due_at: string;
}

export interface PlaygroundReminderCycle {
  /** Empty when the dialog has no series yet. */
  status: '' | 'active' | 'stopped' | 'deferred' | string;
  pushes_sent: number;
  stop_reason?: string;
  next_step?: PlaygroundReminderStep;
}

export interface PlaygroundReminderSimulation {
  step: PlaygroundReminderStep;
  cycle: PlaygroundReminderCycle;
  messages: PlaygroundMessage[];
}

export async function getPlaygroundReminderCycle(
  dialogId: string,
): Promise<PlaygroundReminderCycle> {
  return apiRequest<PlaygroundReminderCycle>(
    `/playground/sessions/${encodeURIComponent(dialogId)}/reminders`,
  );
}

/** Advances the no-reply series by one step; nothing is delivered to a channel. */
export async function simulatePlaygroundReminder(
  dialogId: string,
): Promise<PlaygroundReminderSimulation> {
  return apiRequest<PlaygroundReminderSimulation>(
    `/playground/sessions/${encodeURIComponent(dialogId)}/reminders/simulate`,
    { method: 'POST' },
  );
}

export interface PlaygroundMemoryItem {
  id: string;
  scope: string;
  key?: string;
  content: string;
  importance?: number | null;
  origin: string;
}

export interface PlaygroundMemoryEntity {
  label: string;
  id: string;
  topic?: string;
  domain?: string;
  locale?: string;
  props: Record<string, unknown>;
}

export interface PlaygroundMemoryEdge {
  type: string;
  from_label: string;
  from_id: string;
  to_label: string;
  to_id: string;
}

export interface PlaygroundMemory {
  interlocutor_summary: string;
  dialog_summary: string;
  items: PlaygroundMemoryItem[];
  entities: PlaygroundMemoryEntity[];
  edges: PlaygroundMemoryEdge[];
}

export async function getPlaygroundMemory(dialogId: string): Promise<PlaygroundMemory> {
  const data = await apiRequest<PlaygroundMemory>(
    `/playground/sessions/${encodeURIComponent(dialogId)}/memory`,
  );
  return {
    interlocutor_summary: data.interlocutor_summary ?? '',
    dialog_summary: data.dialog_summary ?? '',
    items: data.items ?? [],
    entities: data.entities ?? [],
    edges: data.edges ?? [],
  };
}
